import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { supabase } from '../config/supabase';
import { authenticatedFetch } from '../utils/apiUtils';

/**
 * AdminLogin Page
 * Handles admin impersonation flow: receives userId, clears old session, and logs in as target user
 * This page should only be accessed with admin privileges via the /admin_login?userId=xxx route
 */
const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent running multiple times
    if (processedRef.current) return;

    // Wait for auth to finish loading and user to be available
    if (loading || !user) {
      return;
    }

    // Check permissions - user should be loaded now
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      showError('You do not have permission to perform this action');
      setIsProcessing(false);
      setTimeout(() => navigate('/dashboard'), 2000);
      return;
    }

    // Mark as processed to prevent re-running
    processedRef.current = true;

    const processAdminLogin = async () => {
      try {
        const userId = searchParams.get('userId');

        if (!userId) {
          showError('No userId provided');
          setIsProcessing(false);
          setTimeout(() => navigate('/admin'), 2000);
          return;
        }

        // First, fetch the user info to get their email
        const userResponse = await authenticatedFetch(`/api/v1/admin/users/${userId}`);
        if (!userResponse.ok) {
          showError('Failed to fetch user information');
          setIsProcessing(false);
          setTimeout(() => navigate('/admin'), 2000);
          return;
        }

        const userData = await userResponse.json();
        const userEmail = userData?.user?.email;

        if (!userEmail) {
          showError('Could not find user email');
          setIsProcessing(false);
          setTimeout(() => navigate('/admin'), 2000);
          return;
        }

        console.log('User email found:', userEmail);

        // Fetch the admin login token from backend using authenticatedFetch
        const response = await authenticatedFetch(`/api/v1/admin/login-as-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, userEmail }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          showError(errorData.error || 'Failed to login as user');
          setIsProcessing(false);
          setTimeout(() => navigate('/admin'), 2000);
          return;
        }

        const data = await response.json();
        if (!data.success || !data.token) {
          showError('Failed to get login token');
          setIsProcessing(false);
          setTimeout(() => navigate('/admin'), 2000);
          return;
        }

        // CRITICAL: Clear ALL localStorage and sessionStorage to remove admin session
        localStorage.clear();
        sessionStorage.clear();

        // Then set the new customer token
        // AuthContext will automatically detect this token and load user data
        localStorage.setItem('auth_token', data.token);

        // Important: Manual trigger to refresh user data in AuthContext
        const context = (window as any).AuthContext;
        if (context && typeof context.refreshUserData === 'function') {
          await context.refreshUserData();
        }

        showSuccess(`Logged in as ${data.user.email}`);
        setIsProcessing(false);

        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Error in admin login:', error);
        showError('Error processing login');
        setIsProcessing(false);
        setTimeout(() => navigate('/admin'), 2000);
      }
    };

    processAdminLogin();
  }, [loading, user, searchParams, navigate, showError, showSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        {isProcessing && <p className="text-gray-300">Processing login...</p>}
      </div>
    </div>
  );
};

export default AdminLogin;

